'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { MapPin, Calendar, Clock, FileText, Camera, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

type LostFormData = {
  lastLocation: string;
  lostDate: string;
  lostTime: string;
  description: string;
};

export default function LostReportForm() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LostFormData>({
    defaultValues: {
      lastLocation: 'Heathrow Airport Terminal 5, London',
      lostDate: '2026-09-04',
      lostTime: '14:30',
      description: 'Black Samsonite Evolve hardshell suitcase, medium size. Has a red ribbon tied to the handle.',
    },
  });

  // Backend integration point: POST /api/recovery/report-lost
  const onSubmit = async (data: LostFormData) => {
    setLoading(true);
    await new Promise((r) => setTimeout(r, 1200));
    setLoading(false);
    setSubmitted(true);
    toast.success('Lost Mode activated — finder alerts are now live.');
  };

  if (submitted) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-2xl p-6 flex items-start gap-4">
        <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
          <CheckCircle size={18} className="text-red-500" />
        </div>
        <div>
          <p className="font-700 text-foreground">Lost Mode Activated</p>
          <p className="text-sm text-muted-foreground mt-1">
            Anyone who scans your RETURNI label will see a secure finder page. You&apos;ll be notified immediately when the label is scanned.
          </p>
          <div className="mt-3 bg-white rounded-xl px-4 py-3 border border-red-100 inline-block">
            <p className="text-xs text-muted-foreground font-500">Recovery Session Code</p>
            <p className="font-800 text-lg text-foreground tabular-nums tracking-widest mt-0.5">R7K4-29</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-border shadow-card overflow-hidden">
      <div className="px-6 py-4 border-b border-border bg-red-50/50 flex items-center gap-3">
        <div className="w-8 h-8 bg-red-100 rounded-xl flex items-center justify-center">
          <MapPin size={15} className="text-red-500" />
        </div>
        <div>
          <h2 className="font-700 text-base text-foreground">Report Lost</h2>
          <p className="text-xs text-muted-foreground">Tell us when and where you last had it</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="p-6 flex flex-col gap-5">
        {/* Last location */}
        <div>
          <label className="block text-sm font-600 text-foreground mb-1.5">
            <span className="flex items-center gap-1.5">
              <MapPin size={13} className="text-muted-foreground" />
              Where was it last seen?
            </span>
          </label>
          <p className="text-xs text-muted-foreground mb-2">Be as specific as possible — this helps finders identify the right area.</p>
          <input
            {...register('lastLocation', { required: 'Please enter the last known location' })}
            type="text"
            placeholder="e.g. Heathrow Airport Terminal 5"
            className={`w-full bg-secondary rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground outline-none border transition-colors focus:border-accent focus:bg-white ${
              errors.lastLocation ? 'border-red-400' : 'border-border'
            }`}
          />
          {errors.lastLocation && (
            <p className="text-red-500 text-xs mt-1.5">{errors.lastLocation.message}</p>
          )}
        </div>

        {/* Date and time */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-600 text-foreground mb-1.5">
              <span className="flex items-center gap-1.5">
                <Calendar size={13} className="text-muted-foreground" />
                Date lost
              </span>
            </label>
            <input
              {...register('lostDate', { required: 'Date is required' })}
              type="date"
              className={`w-full bg-secondary rounded-xl px-4 py-3 text-sm text-foreground outline-none border transition-colors focus:border-accent focus:bg-white ${
                errors.lostDate ? 'border-red-400' : 'border-border'
              }`}
            />
            {errors.lostDate && (
              <p className="text-red-500 text-xs mt-1.5">{errors.lostDate.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-600 text-foreground mb-1.5">
              <span className="flex items-center gap-1.5">
                <Clock size={13} className="text-muted-foreground" />
                Approximate time
              </span>
            </label>
            <input
              {...register('lostTime')}
              type="time"
              className="w-full bg-secondary rounded-xl px-4 py-3 text-sm text-foreground outline-none border border-border transition-colors focus:border-accent focus:bg-white"
            />
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-600 text-foreground mb-1.5">
            <span className="flex items-center gap-1.5">
              <FileText size={13} className="text-muted-foreground" />
              Description
            </span>
          </label>
          <p className="text-xs text-muted-foreground mb-2">Describe the item — colour, distinguishing features, contents if relevant.</p>
          <textarea
            {...register('description', { required: 'Please add a description to help finders identify your item' })}
            rows={3}
            placeholder="Black Samsonite suitcase, medium size, red ribbon on handle..."
            className={`w-full bg-secondary rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground outline-none border resize-none transition-colors focus:border-accent focus:bg-white ${
              errors.description ? 'border-red-400' : 'border-border'
            }`}
          />
          {errors.description && (
            <p className="text-red-500 text-xs mt-1.5">{errors.description.message}</p>
          )}
        </div>

        {/* Photo upload placeholder */}
        <div>
          <label className="block text-sm font-600 text-foreground mb-1.5">
            <span className="flex items-center gap-1.5">
              <Camera size={13} className="text-muted-foreground" />
              Add a photo (optional)
            </span>
          </label>
          <div className="border-2 border-dashed border-border rounded-xl px-6 py-5 text-center hover:border-accent/40 transition-colors cursor-pointer bg-secondary/30">
            <Camera size={20} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground font-500">Tap to add a photo</p>
            <p className="text-xs text-muted-foreground/60 mt-0.5">Helps finders confirm it&apos;s the right item</p>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="btn-primary py-3.5 text-sm w-full justify-center"
          style={{ minWidth: '160px' }}
        >
          {loading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Activating Lost Mode...
            </>
          ) : (
            'Activate Lost Mode'
          )}
        </button>

        <p className="text-xs text-muted-foreground text-center">
          RETURNI does not guarantee recovery. Location availability depends on finder cooperation and device capabilities.
        </p>
      </form>
    </div>
  );
}